import React, { useRef, useState } from 'react';
import { AI_JOB_MODELS, AI_JOB_MODEL_DEFAULT_LABEL, aiJobModelLabel } from '../../lib/aiJobs';
import { Icon, Popover } from '../ui';

/**
 * ModelPicker — which LLM runs a queue job. Custom control (no native
 * <select>); the menu floats through ui/Popover so it works inside sortable
 * rows, side panels and cards. `null` means "Agent default" (the listener
 * passes no --model flag).
 *
 *   'chip'  — compact pill for queue rows. Read-only when no onChange.
 *   'field' — full-width form control with an eyebrow label, for enqueue panels.
 */
export interface ModelPickerProps {
  value: string | null;
  /** Omit to render a read-only chip (e.g. a running or finished job). */
  onChange?: (model: string | null) => void;
  disabled?: boolean;
  variant?: 'chip' | 'field';
  label?: string;
  hint?: React.ReactNode;
  style?: React.CSSProperties;
}

const DEFAULT_ID = '';

export default function ModelPicker({
  value,
  onChange,
  disabled = false,
  variant = 'chip',
  label = 'Model',
  hint,
  style,
}: ModelPickerProps) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const options = [{ id: DEFAULT_ID, label: AI_JOB_MODEL_DEFAULT_LABEL }, ...AI_JOB_MODELS];
  const current = value ?? DEFAULT_ID;
  const currentLabel = aiJobModelLabel(value);
  const readOnly = !onChange;
  const isChip = variant === 'chip';

  const pick = (id: string) => {
    onChange?.(id === DEFAULT_ID ? null : id);
    setOpen(false);
  };

  const toggle = () => {
    if (disabled || readOnly) return;
    setOpen((o) => !o);
    setHi(Math.max(0, options.findIndex((o) => o.id === current)));
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (readOnly || disabled) return;
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      toggle();
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => Math.min(options.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (hi >= 0) pick(options[hi].id);
    }
  };

  // Queue rows open the job on click and start a drag on mousedown / keydown.
  // The picker (and its portaled menu, which bubbles through React) must not
  // leak any of those up.
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  const chipStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '0 7px 0 8px',
    borderRadius: 'var(--radius-pill)',
    border: '1px solid ' + (open ? 'var(--accent)' : 'var(--border-hairline)'),
    background: value ? 'var(--accent-soft)' : 'var(--surface-raised)',
    color: value ? 'var(--accent-text)' : 'var(--text-muted)',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--text-2xs)',
    fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
    letterSpacing: 'var(--tracking-label)',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    cursor: readOnly ? 'default' : disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    transition: 'border-color var(--dur-base) var(--ease-out), background var(--dur-base) var(--ease-out)',
  };

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    width: '100%',
    height: 'var(--control-md)',
    padding: '0 11px 0 12px',
    background: 'var(--surface-raised)',
    border: '1px solid ' + (open ? 'var(--accent)' : 'var(--border-hairline)'),
    borderRadius: 'var(--radius-md)',
    color: 'var(--text-body)',
    fontFamily: 'var(--font-ui)',
    fontSize: 'var(--text-sm)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    textAlign: 'left',
    whiteSpace: 'nowrap',
    transition: 'border-color var(--dur-base) var(--ease-out)',
  };

  return (
    <div
      onClick={stop}
      onMouseDown={stop}
      onKeyDown={stop}
      style={{ position: 'relative', display: isChip ? 'inline-flex' : 'block', flexShrink: 0, ...style }}
    >
      {!isChip && label ? <div className="t-eyebrow" style={{ marginBottom: '7px' }}>{label}</div> : null}
      <button
        ref={buttonRef}
        type="button"
        // Phones: a finger-sized chip, narrower so a queue row's chips and actions share one line.
        className={
          isChip
            ? `max-w-[110px] md:max-w-[150px] ${readOnly ? 'h-[20px]' : 'pressable h-[30px] md:h-[20px]'}`
            : readOnly
              ? undefined
              : 'pressable'
        }
        aria-haspopup={readOnly ? undefined : 'listbox'}
        aria-expanded={readOnly ? undefined : open}
        aria-label={isChip ? `${label}: ${currentLabel}` : undefined}
        title={isChip ? (readOnly ? `Ran on ${currentLabel}` : `Model: ${currentLabel}`) : undefined}
        disabled={disabled}
        tabIndex={readOnly ? -1 : 0}
        onClick={toggle}
        onKeyDown={onKey}
        style={isChip ? chipStyle : fieldStyle}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentLabel}</span>
        {readOnly ? null : (
          <Icon
            name="chevron-down"
            size={isChip ? 10 : 12}
            style={{
              color: 'currentColor',
              opacity: 0.7,
              flexShrink: 0,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform var(--dur-base) var(--ease-out)',
            }}
          />
        )}
      </button>
      {!isChip && hint ? (
        <div style={{ marginTop: '6px', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', lineHeight: 1.5 }}>
          {hint}
        </div>
      ) : null}

      <Popover
        open={open && !readOnly}
        anchorEl={buttonRef.current}
        onClose={() => setOpen(false)}
        matchWidth={!isChip}
        minWidth={isChip ? 200 : undefined}
        align={isChip ? 'right' : 'left'}
        role="listbox"
        style={{ maxHeight: '264px' }}
      >
        {options.map((o, i) => {
          const isSel = o.id === current;
          return (
            <button
              key={o.id || 'default'}
              type="button"
              role="option"
              aria-selected={isSel}
              className="pressable"
              onClick={() => pick(o.id)}
              onMouseEnter={() => setHi(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                width: '100%',
                padding: '7px 10px',
                border: 'none',
                textAlign: 'left',
                background: hi === i ? 'var(--surface-active)' : 'transparent',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-sm)',
                fontWeight: (isSel ? 'var(--weight-medium)' : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
                color: 'var(--text-body)',
                transition: 'background var(--dur-fast) var(--ease-out)',
              }}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {o.label}
                {o.id ? (
                  <span
                    style={{
                      display: 'block',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--text-2xs)',
                      color: 'var(--text-faint)',
                    }}
                  >
                    {o.id}
                  </span>
                ) : (
                  <span style={{ display: 'block', fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
                    No --model flag; the listener uses its own default
                  </span>
                )}
              </span>
              {isSel ? <Icon name="check" size={12} style={{ color: 'var(--accent-text)', flexShrink: 0 }} /> : null}
            </button>
          );
        })}
      </Popover>
    </div>
  );
}
